import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const steps = [
  {
    number: "01",
    title: "Understand Your Game",
    description: "Your name should feel like it belongs to your game's world. Define what makes it unique.",
    tags: ["Fantasy", "Sci-Fi", "Horror", "Action", "Puzzle"],
  },
  {
    number: "02",
    title: "Brainstorm Keywords",
    description: "Pull words from the game's themes, emotions, and imagery. The best names often hide in plain sight.",
    tags: ["Shadow", "Storm", "Forge", "Fury", "Chaos", "Void"],
  },
  {
    number: "03",
    title: "Combine & Experiment",
    description: "Smash words together, flip them, abbreviate them. The stranger the combination, the more memorable.",
    tags: ["ShadowForge", "NeoGenesis", "Knightmare", "VoidClash"],
  },
  {
    number: "04",
    title: "Check Availability",
    description: "A great name is useless if it's taken. Verify across domains, social handles, and game platforms.",
    tags: ["Domain", "Steam", "Twitter / X", "Instagram"],
  },
  {
    number: "05",
    title: "Test for Impact",
    description: "Say it out loud. Does it stick? Does it sound like it belongs in a loading screen?",
    tags: ["Memorable", "Pronounceable", "Unique", "On-brand"],
  },
];

function Creation() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [visible, setVisible] = useState({});
  const [ctaVisible, setCtaVisible] = useState(false);

  const heroRef = useRef(null);
  const stepRefs = useRef([]);
  const ctaRef = useRef(null);

  useEffect(() => {
    const observe = (ref, onEnter) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { onEnter(); obs.disconnect(); } },
        { threshold: 0.15 }
      );
      obs.observe(ref);
      return obs;
    };

    const heroObs = observe(heroRef.current, () => setHeroVisible(true));
    const ctaObs  = observe(ctaRef.current,  () => setCtaVisible(true));

    const stepObs = stepRefs.current.map((ref, i) =>
      observe(ref, () => setVisible(v => ({ ...v, [i]: true })))
    );

    return () => {
      heroObs?.disconnect();
      ctaObs?.disconnect();
      stepObs.forEach(o => o?.disconnect());
    };
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-5 py-12">

      {/* Hero */}
      <div
        ref={heroRef}
        style={{ transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        className={`mb-12 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <p className="text-[#8B5CF6] text-xs uppercase tracking-widest font-semibold mb-3">Guide</p>
        <h1 className="nav-brand text-4xl sm:text-5xl font-bold text-[#F5F5F5] leading-tight mb-4">
          How to craft a<br />
          <span className="text-[#8B5CF6]">legendary game name</span>
        </h1>
        <p className="text-[#F5F5F5]/60 text-base max-w-xl">
          A great name sets the tone before a single pixel is seen. Follow these steps to build one that sticks.
        </p>
      </div>

      {/* Steps */}
      <div className="flex flex-col gap-4">
        {steps.map((step, i) => (
          <div
            key={step.number}
            ref={el => stepRefs.current[i] = el}
            style={{
              transition: 'opacity 0.5s ease, transform 0.5s ease',
              transitionDelay: `${i * 80}ms`,
            }}
            className={`bg-[#1a1a1a] border border-[#222222] rounded-xl p-6 flex gap-5 ${
              visible[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
          >
            <span className="nav-brand text-3xl font-bold text-[#8B5CF6]/30 leading-none select-none w-10 shrink-0">
              {step.number}
            </span>
            <div>
              <h2 className="nav-brand text-lg font-semibold text-[#F5F5F5] mb-1">{step.title}</h2>
              <p className="text-[#F5F5F5]/60 text-sm leading-relaxed mb-3">{step.description}</p>
              <div className="flex flex-wrap gap-2">
                {step.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div
        ref={ctaRef}
        style={{ transition: 'opacity 0.6s ease, transform 0.6s ease', transitionDelay: '200ms' }}
        className={`mt-10 text-center ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
      >
        <p className="text-[#F5F5F5]/50 text-sm mb-4">Ready to build yours?</p>
        <Link
          to="/create"
          className="inline-block bg-[#8B5CF6] hover:bg-[#7C3AED] text-[#F5F5F5] font-semibold px-8 py-3 rounded-lg transition-colors"
        >
          Start Creating
        </Link>
      </div>

    </div>
  );
}

export default Creation;
